<template>
  <v-dialog v-model="dialog" width="500" @click:outside="cancel" :persistent="isLoading">
    <v-card>

      <v-alert
        v-model="alertFlg"
        type="error"
        density="compact"
        :title=errorMsg
        :text="$t('common.message.retry')"
        closable
        class="ma-2"
      />

      <v-card-text
        class="pa-4 pre-line grey--text text--darken-4 text-subtitle-1 px-5"
      >
        <p v-if="dialogData.actionType === 0" class="mb-4">
          {{ $t('tanaoroshiConfirmDialog.message.comfirmClear') }}
        </p>
        <p v-if="dialogData.actionType === 1" class="mb-4">
          {{ $t('tanaoroshiConfirmDialog.message.confirmAndCooperate') }}
        </p>
        <p v-if="dialogData.actionType === 2" class="mb-4">
          {{ $t('tanaoroshiConfirmDialog.message.confirm') }}
          <br>
          <v-text style="color: #FF0000;">{{ $t('tanaoroshiConfirmDialog.message.confirmAnnotation') }}</v-text>
        </p>
        <p v-if="dialogData.actionType === 3" class="mb-4">
          {{ $t('tanaoroshiConfirmDialog.message.confirmAndCooperateApv') }}
        </p>
        <p v-if="this.isCsvDownload()" class="mb-4">
          {{ useDatDownload ? $t('tanaoroshiConfirmDialog.message.downloadDat') : $t('tanaoroshiConfirmDialog.message.downloadCsv') }}
        </p>

        <template
          v-if="dialogData.actionType === 1 || dialogData.actionType === 2 || dialogData.actionType === 3"
        >
          <div
            v-for="z in dialogData.tanaoroshi.zoneList"
            :key="z.zoneId"
            class="text-h6 d-flex justify-end mb-1"
          >
            <span>{{ z.zoneName }}</span>
            <span>{{ z.readSuuryo }} / {{ z.planSuuryo }}</span>
            <span>({{ z.readPrecision }})</span>
          </div>
          <v-text-field
            density="compact"
            variant="outlined"
            hide-details="auto"
            v-model="staffCode"
            :label="staffCodeLabel"
            :counter="staffCodeLength"
            :maxLength="staffCodeLength"
            :disabled="isLoading"
            :rules="staffCodeRules"
          />
        </template>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="cancel" :disabled="isLoading"> {{ $t('common.action.cancel') }} </v-btn>
          <v-btn
            color="indigo darken-1"
            dark
            @click="ok"
            :loading="isLoading"
            :disabled="invalidInput"
          >
          {{ $t('common.action.ok') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
</template>

<script>
import Vue from 'vue';
import api from "@/apis/staff";
import constant from "@/utils/constant"
import utils from "@/utils";
import store from "@/store";

/** datダウンロードでAPIがファイル名を返さなかった場合のファイル名 */
const DAT_FALLBACK_FILE_NAME = "ht_tana.dat";

export default {
  props: {
    dialogData: {
      type: Object, 
      required: true, 
    },
    handleOk: {
      type: Function,
      required: true, 
    },
    handleCancel: {
      type: Function,
      required: true,
    },
  },
  computed: {
    // 連携時の警告メッセージ
    coopWarn() {
      if(this.dialogData.tanaoroshi?.confirmedDatetime == null || this.dialogData.tanaoroshi?.confirmedDatetime.length != 14) return {flag: false, date: 0}
      const warnDaySub = Math.floor((new Date() - new Date(this.dialogData.tanaoroshi.confirmedDatetime.substring(0,4), parseInt(this.dialogData.tanaoroshi.confirmedDatetime.substring(4,6)) - 1, this.dialogData.tanaoroshi.confirmedDatetime.substring(6,8))) / 86400000)
      return {
        date: warnDaySub,
        flag: warnDaySub > constant.TANA_CONFIRM_WARN_THRESHOLD ? true : false
      };
    },
    
    // 確定者社員コードの入力欄ラベル。桁数ちょうどのときだけ桁数を添える（例: 確定者社員コード（6桁））
    staffCodeLabel() {
      return this.isExactLength
        ? this.$t('tanaoroshiConfirmDialog.label.staffCodeFixedLength', { length: this.staffCodeLength })
        : this.$t('tanaoroshiConfirmDialog.label.staffCode');
    },

    // 確定者社員コードの入力チェック。桁数ちょうどのときは桁数も検査する
    // （API の TanaoroshiConfirmValidator も同じ設定で検証する）
    staffCodeRules() {
      const rules = [this.rules.checkStaffCode];
      if (this.isExactLength) rules.push(this.rules.checkStaffCodeLength);
      return rules;
    },

    // validation
    invalidInput(){
      if (this.isCsvDownload()) return false;
      if (!this.isValidStaffCode(this.staffCode)) return true;
      if (this.isExactLength) return this.staffCode.length !== this.staffCodeLength;
      if((this.staffCode.length > 0 && this.staffCode.length <= this.staffCodeLength)) return false;
      return true;
    },
  },
  data() {
    return {
      staffCode: "",
      dialog: true,
      isLoading: false,
      errorMsg: "",
      alertFlg: false,
      coopWarning: false,
      coopDateSub: 2,
      // 担当者コードの桁数と、ちょうどその桁数を要求するか
      // （設定マスタ TANAOROSHI_STAFF_CODE_LENGTH / _MODE。ログイン時に取得済みの値を使う）
      staffCodeLength: utils.getTanaoroshiStaffCodeLength(),
      isExactLength: utils.isTanaoroshiStaffCodeExactLength(),
      // 棚卸実績のダウンロードをCSVではなくdatで行う(FO向け。設定マスタ USE_TANAOROSHI_DAT_DOWNLOAD)
      useDatDownload: !!store.getFeatureFlags().useTanaoroshiDatDownload,
      // dat運用時、確定直後にdatを自動ダウンロードする(設定マスタ CAN_DOWNLOAD_TANAOROSHI_CSV)
      autoDownloadDatOnConfirm: !!store.getFeatureFlags().useTanaoroshiDatDownload
        && !!store.getFeatureFlags().canDownloadTanaoroshiCsv,
      rules: {
        checkStaffCode: v => {
          return this.isValidStaffCode(v) || this.$t('tanaoroshiConfirmDialog.validation.halfAlnum');
        },
        checkStaffCodeLength: v => {
          return (v ?? "").length === this.staffCodeLength
            || this.$t('tanaoroshiConfirmDialog.validation.fixedLength', { length: this.staffCodeLength });
        },
      },
    };
  },
  watch: {
    "dialogData.tanaoroshi": function () {
      this.errorMsg = "";
      this.staffCode = "";
    },
  },
  mounted() {
    // do nothing.
  },
  methods: {
    isValidStaffCode(v) {
      return /^[0-9A-Za-z]*$/.test(v);
    },
    isCsvDownload() {
      return this.dialogData.actionType == 4;
    },
    cancel() {
      if(this.isLoading) return;
      this.handleCancel();
    },
    ok() {
      this.isLoading = true;

      if (this.isCsvDownload()) {
        if (this.useDatDownload) this.downloadDat();
        else this.downloadCsv();
        return;
      }
      //apiに突っ込む
      api.post("/tanaoroshi/confirm", {
        tanaoroshiId: this.dialogData?.tanaoroshi.tanaoroshiId ?? null,
        staffId: this.staffCode,
        shopCode: this.dialogData?.shopCode ?? "",
        releyFlag: this.dialogData?.actionType == 3 ? 1 : 0,
      })
        .then((response) => {

          this.isLoading = false;
          if(response.data?.status == 0) {
            this.errorMsg = "";
            if (this.autoDownloadDatOnConfirm) {
              // 確定処理成功時に自動でdatダウンロードを実行し、完了後にダイアログを閉じる
              this.isLoading = true;
              this.downloadDat();
              return;
            }
            this.handleOk(this.dialogData.actionType);
          }
          else {  
            this.errorMsg = response.data?.errorMessage;
            this.alertFlg = true;
          }

        })
        .catch((error) => {
          //do nothing
          this.isLoading = false;
          if(error.response?.status == 404) this.errorMsg = this.$t('common.message.notFound');
          else if(error.response?.status == 400) this.errorMsg = error.response.data.errorMessage;
          else if(error.response?.data?.status == 1) this.errorMsg = this.$t('tanaoroshiConfirmDialog.message.error');
          else this.errorMsg = this.$t('tanaoroshiConfirmDialog.message.unknownError');
          this.alertFlg = true;
        });
    },
    // CSVダウンロード処理
    downloadCsv() {
      api.post("/tanaoroshi/download-csv", { tanaoroshiId: this.dialogData.tanaoroshi.tanaoroshiId })
        .then((response) => {

          if (response.status == 1) return;

          let dataArr = [];

          response.data.results.csvRecordList.forEach(element => {
            dataArr.push(element.join(",") + "\r\n");
          });
          const stamp = utils.csvTimeStamp(new Date())
          const prefix = this.$t('tanaoroshiConfirmDialog.fileNamePrefix.csv');
          utils.csvDownload(dataArr.join(""), `${prefix}${stamp}`);

          this.isLoading = false;
          this.errorMsg = "";
          this.handleOk(this.dialogData.actionType);
        })
        .catch((error) => {
          this.isLoading = false;this.errorMsg = this.$t('common.message.unknownError');
          this.alertFlg = true;
        })
    },
    // datダウンロード処理(FO向け)
    async downloadDat() {
      try {
        const response = await api.fetchBlob("/tanaoroshi/download-dat", { tanaoroshiId: this.dialogData.tanaoroshi.tanaoroshiId });
        const url = URL.createObjectURL(new Blob([response.data], { type: response.headers["content-type"] }));
        const link = document.createElement("a");
        link.href = url;
        link.download = this.extractDownloadFileName(response.headers["content-disposition"], DAT_FALLBACK_FILE_NAME);
        link.click();
        URL.revokeObjectURL(url);

        this.isLoading = false;
        this.errorMsg = "";
        this.handleOk(this.dialogData.actionType);
      } catch (error) {
        this.isLoading = false;
        const message = await this.extractDownloadErrorMessage(error);
        this.errorMsg = message || this.$t('common.message.unknownError');
        this.alertFlg = true;
      }
    },
    /**
     * datダウンロード失敗時のメッセージを抽出(エラー時はJSONがBlobで返る)
     * @param error
     * @return {Promise<string|null>}
     */
    async extractDownloadErrorMessage(error) {
      const data = error?.response?.data;
      if (data && data instanceof Blob) {
        const text = await data.text();
        if (!text) return null;
        try {
          const json = JSON.parse(text);
          return json.errorMessage || json.message || null;
        } catch {
          return text;
        }
      }
      return null;
    },
    /**
     * content-dispositionからファイル名を取得。取得できない場合はfallbackNameを返す
     * @param disposition
     * @param fallbackName
     */
    extractDownloadFileName(disposition, fallbackName) {
      if (disposition && disposition.indexOf('attachment') !== -1) {
        const matches = /filename\*=UTF-8''(.+)/.exec(disposition);
        if (matches != null && matches[1]) {
          // URLエンコードされた文字列をもとのファイル名に戻す
          return decodeURIComponent(matches[1]);
        }
      }
      return fallbackName;
    }
  },
};
</script>
<style>
.pre-line {
  white-space: pre-line;
}
</style>
